import {
  Box,
  Card,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import Iconify from '../iconify';
import { useGetPickem } from '@/api/pickems/getPickem.ts';
import { AnyFieldApi } from '@tanstack/react-form';
import { useAppSearchParams } from '@/hooks/useAppSearchParams.ts';

interface EditTextSettingsProps {
  lang: string;
  form: any;
  setAssetsTarget: (key: string) => void;
}

export const EditTextSettings = ({
  lang,
  form,
  setAssetsTarget,
}: EditTextSettingsProps) => {
  const { pickemId } = useAppSearchParams();
  const { isLoading } = useGetPickem(pickemId);

  const deleteImage = (key: string) => {
    form.setFieldValue(key, '');
  };

  if (isLoading) {
    return (
      <Card sx={{ padding: 4 }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'center' }}
          style={{ padding: 20 }}
        >
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  return (
    <Card sx={{ padding: 4 }}>
      <Grid container>
        {/* Title, Description, Prize */}
        <Grid item xs={12} sx={{ mb: 2 }}>
          {/* Title */}
          <form.Field
            name={`title_${lang}`}
            children={({ state, handleChange, handleBlur }: AnyFieldApi) => (
              <TextField
                label={`Pickem Title ${lang}`}
                fullWidth
                variant='standard'
                value={state.value}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sx={{ mb: 2 }}>
          {/* Description */}
          <form.Field
            name={`description_${lang}`}
            children={(props: AnyFieldApi) => (
              <TextField
                label={`Description ${lang}`}
                fullWidth
                variant='standard'
                {...props}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sx={{ mb: 2 }}>
          <form.Field
            name={`prize_text_${lang}`}
            children={({ state, handleChange, handleBlur }: AnyFieldApi) => (
              <TextField
                label={`Prize Text ${lang}`}
                fullWidth
                variant='standard'
                value={state.value}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
              />
            )}
          />
        </Grid>

        {/* Prizing (for GameZone)*/}
        <Grid item xs={12}>
          <Typography variant='h6' gutterBottom>
            Prizes Description
          </Typography>
        </Grid>

        <Grid item xs={12} mb={4}>
          <Grid container gap={2}>
            <Grid item xs={5}>
              <form.Field
                name={`prize_text_${lang}`}
                children={({
                  state,
                  handleChange,
                  handleBlur,
                }: AnyFieldApi) => (
                  <TextField
                    label={`Main Prize Title ${lang}`}
                    fullWidth
                    variant='standard'
                    value={state.value}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                  />
                )}
              />
            </Grid>
            <Grid item xs={5}>
              <form.Field
                name={`prizes_desc_${lang}.main_desc`}
                children={({
                  state,
                  handleChange,
                  handleBlur,
                }: AnyFieldApi) => (
                  <TextField
                    label={`Main Prize Desc ${lang}`}
                    fullWidth
                    variant='standard'
                    value={state.value}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container gap={2}>
            <Grid item xs={5}>
              <form.Field
                name={`prizes_desc_${lang}.secondary_title`}
                children={({
                  state,
                  handleChange,
                  handleBlur,
                }: AnyFieldApi) => (
                  <TextField
                    label={`Secondary Title ${lang}`}
                    fullWidth
                    variant='standard'
                    value={state.value}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                  />
                )}
              />
            </Grid>
            <Grid item xs={5}>
              <form.Field
                name={`prizes_desc_${lang}.secondary_desc`}
                children={({
                  state,
                  handleChange,
                  handleBlur,
                }: AnyFieldApi) => (
                  <TextField
                    label={`Secondary Prize Desc ${lang}`}
                    fullWidth
                    variant='standard'
                    value={state.value}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Images */}
        <form.Field
          name={`main_image_${lang}`}
          children={({ state }: AnyFieldApi) => {
            return (
              <Grid item xs={4}>
                <Box>Main Image {lang}</Box>
                {state.value && state.value !== 'null' ? (
                  <>
                    <img
                      style={{
                        margin: '0 auto',
                        width: 100,
                        background: 'grey',
                      }}
                      alt={`main ${lang} img`}
                      src={state.value}
                    />
                    <Iconify
                      icon='eva:edit-outline'
                      sx={{
                        width: 20,
                        height: 20,
                        cursor: 'pointer',
                      }}
                      onClick={() => setAssetsTarget(`main_image_${lang}`)}
                    />
                    <Iconify
                      icon='eva:close-outline'
                      sx={{
                        width: 20,
                        height: 20,
                        cursor: 'pointer',
                      }}
                      onClick={() => deleteImage(`main_image_${lang}`)}
                    />
                  </>
                ) : (
                  <Iconify
                    icon='eva:plus-circle-outline'
                    sx={{
                      width: 20,
                      height: 20,
                      cursor: 'pointer',
                      marginTop: 2,
                      marginBottom: 2,
                    }}
                    onClick={() => setAssetsTarget(`main_image_${lang}`)}
                  />
                )}
              </Grid>
            );
          }}
        />

        <form.Field
          name={`prize_image_${lang}`}
          children={({ state }: AnyFieldApi) => {
            return (
              <Grid item xs={4}>
                <Box>Prize Image {lang}</Box>
                {state.value && state.value !== 'null' ? (
                  <>
                    <img
                      style={{
                        margin: '0 auto',
                        width: 100,
                        background: 'grey',
                      }}
                      alt={`prize ${lang} img`}
                      src={state.value}
                    />
                    <Iconify
                      icon='eva:edit-outline'
                      sx={{
                        width: 20,
                        height: 20,
                        cursor: 'pointer',
                      }}
                      onClick={() => setAssetsTarget(`prize_image_${lang}`)}
                    />
                    <Iconify
                      icon='eva:close-outline'
                      sx={{
                        width: 20,
                        height: 20,
                        cursor: 'pointer',
                      }}
                      onClick={() => deleteImage(`prize_image_${lang}`)}
                    />
                  </>
                ) : (
                  <Iconify
                    icon='eva:plus-circle-outline'
                    sx={{
                      width: 20,
                      height: 20,
                      cursor: 'pointer',
                      marginTop: 2,
                      marginBottom: 2,
                    }}
                    onClick={() => setAssetsTarget(`prize_image_${lang}`)}
                  />
                )}
              </Grid>
            );
          }}
        />

        <form.Field
          name={`gamezone_image_${lang}`}
          children={({ state }: AnyFieldApi) => {
            return (
              <Grid item xs={4}>
                <Box>Gamezone Image {lang}</Box>
                {state.value && state.value !== 'null' ? (
                  <>
                    <img
                      style={{
                        margin: '0 auto',
                        width: 100,
                        background: 'grey',
                      }}
                      alt={`gamezone ${lang} img`}
                      src={state.value}
                    />
                    <Iconify
                      icon='eva:edit-outline'
                      sx={{
                        width: 20,
                        height: 20,
                        cursor: 'pointer',
                      }}
                      onClick={() => setAssetsTarget(`gamezone_image_${lang}`)}
                    />
                    <Iconify
                      icon='eva:close-outline'
                      sx={{
                        width: 20,
                        height: 20,
                        cursor: 'pointer',
                      }}
                      onClick={() => deleteImage(`gamezone_image_${lang}`)}
                    />
                  </>
                ) : (
                  <Iconify
                    icon='eva:plus-circle-outline'
                    sx={{
                      width: 20,
                      height: 20,
                      cursor: 'pointer',
                      marginTop: 2,
                      marginBottom: 2,
                    }}
                    onClick={() => setAssetsTarget(`gamezone_image_${lang}`)}
                  />
                )}
              </Grid>
            );
          }}
        />
      </Grid>
    </Card>
  );
};
